import '@fontsource-variable/inter/wght.css';

import { grey } from '@mui/material/colors';
import { alpha, createTheme } from '@mui/material/styles';

import { buildDesignTokens, composeAppPalette, isDefaultBrandPrimary } from './cssTokens';
import { generateBrandPalette } from './generateBrandPalette';
import { getPalette } from './palettes';
import { ODL_STYLE_GUIDE } from './styleGuide';

export { ThemeSchemeProvider, useColorScheme } from './ThemeSchemeProvider';
export { TenantThemeProvider, useTenantTheme } from './TenantThemeProvider';
export { default as BrandLogo } from './BrandLogo';
export { default as ColorSchemeToggle } from './ColorSchemeToggle';

// Required-field marker: MUI renders a "*" asterisk on required labels;
// render it as a small filled dot in the error color instead. Shared by
// MuiInputLabel (text fields, selects, autocompletes) and MuiFormLabel
// (radio/checkbox group labels) so every required label reads the same.
// The filled input surface: bordered rounded box, no underline. Shared by
// MuiFilledInput (TextFields, Selects, Autocompletes) and the date pickers'
// MuiPickersFilledInput so a picker field and a text field in the same row
// render as one input family.
const filledInputRootStyle = ({ theme: t }) => ({
  backgroundColor: 'var(--input-bg)',
  borderRadius: 'var(--button-radius)',
  border: '1px solid var(--input-border)',
  boxShadow: 'var(--input-shadow)',
  transition:
    'background-color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard), box-shadow var(--duration-normal) var(--easing-standard)',
  overflow: 'hidden',
  '&:hover': {
    borderColor: 'var(--input-border-hover)',
  },
  '&.Mui-focused': {
    backgroundColor: 'var(--input-bg)',
    borderColor: 'var(--input-focus-border)',
    boxShadow: 'var(--input-focus-shadow), var(--input-shadow)',
  },
  '&.Mui-error': {
    borderColor: t.palette.error.main,
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(t.palette.error.main, 0.18)}, var(--input-shadow)`,
    },
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--input-disabled-bg)',
    color: 'var(--input-disabled-color)',
    borderColor: 'var(--input-disabled-color)',
    boxShadow: 'none',
  },
  '@media (forced-colors: active)': {
    borderColor: 'CanvasText',
  },
});

const requiredDotAsterisk = ({ theme: t }) => ({
  // Hide the "*" glyph but keep its inline box (for spacing + font metrics),
  // then center a 6px dot over it so the marker sits mid-text in any font.
  // The em margin keeps the dot clear of the label text at every label
  // size (shrunk outlined labels included).
  color: 'transparent',
  position: 'relative',
  marginLeft: '0.35em',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: t.palette.error.main,
  },
});

const focusVisible = baseShadow => ({
  '&.Mui-focusVisible': {
    boxShadow: baseShadow ? `${baseShadow}, var(--shadow-focus)` : 'var(--shadow-focus)',
    outline: 'none',
  },
  '@media (forced-colors: active)': {
    '&.Mui-focusVisible': {
      boxShadow: 'none',
      outline: '2px solid Highlight',
      outlineOffset: 2,
    },
  },
});

// MUI Paper elevations otherwise retain the framework's crisp black shadows.
// Collapse all 24 levels into the same three ambient tiers as app surfaces.
const smoothShadows = Array.from({ length: 25 }, (_, elevation) => {
  if (elevation === 0) return 'none';
  if (elevation === 1) return 'var(--shadow-sm)';
  if (elevation <= 8) return 'var(--shadow-md)';
  return 'var(--shadow-lg)';
});

const raisedSurface = {
  boxShadow: 'var(--button-shadow)',
  transition:
    'background-color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard), box-shadow var(--duration-fast) var(--easing-standard)',
  '&:hover': {
    boxShadow: 'var(--button-shadow-hover)',
  },
  '&:active': {
    boxShadow: 'var(--button-shadow-active)',
  },
  ...focusVisible('var(--button-shadow)'),
  '&.Mui-disabled': {
    boxShadow: 'none',
  },
};

// Create the app's MUI theme.  `options.scheme` selects the M3
// palette ('light' | 'dark'); when omitted, defaults to 'light'.
// `options.primaryColor` / `options.secondaryColor` are optional hex strings
// supplied by ThemeSchemeProvider once the /api/tenant/ fetch resolves. Brand
// colors generate accessible accent roles; composeAppPalette keeps backgrounds,
// text, and outlines on the stable neutral app chrome.
//
// Backward compat: the previous signature was
// `createOutdoorlinkTheme(primarySwatch, secondarySwatch)` and both
// args were ignored (per the long-standing note that per-customer
// color customization was off).  Callers using that shape still work
// — we detect the string-shaped first arg and treat it as legacy.
export const createOutdoorlinkTheme = (optionsOrLegacy, _legacySecondary) => {
  const options =
    optionsOrLegacy && typeof optionsOrLegacy === 'object' && !Array.isArray(optionsOrLegacy) ? optionsOrLegacy : {};
  const scheme = options.scheme === 'dark' ? 'dark' : 'light';
  // The stock brand primary already ships as the style-guide-pinned palette;
  // reseeding from it would tonal-wash the exact brand hex (isDefaultBrandPrimary).
  const brandPalette = isDefaultBrandPrimary(options.primaryColor)
    ? null
    : generateBrandPalette(options.primaryColor, options.secondaryColor, scheme);
  const palette = composeAppPalette(getPalette(scheme), brandPalette, scheme);

  const theme = createTheme({
    shadows: smoothShadows,
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: 'var(--font-family-sans)',
      fontSize: 15,
      body1: {
        fontSize: 'var(--font-md)',
        lineHeight: 1.55,
      },
      body2: {
        fontSize: 'var(--font-sm)',
        lineHeight: 1.5,
      },
      h4: {
        fontSize: '2rem',
        fontWeight: 650,
        lineHeight: 1.15,
        letterSpacing: '-0.025em',
        fontVariantNumeric: 'tabular-nums',
      },
      h5: {
        letterSpacing: '-0.012em',
      },
      button: {
        fontWeight: 600,
        letterSpacing: '0.005em',
      },
    },
    breakpoints: {
      // Custom `mobile` step (768px) is the persistent → overlay nav cutoff.
      // AppNav and LeftNav both gate on `down('mobile')` so they never
      // disagree about the drawer mode. The default steps are kept intact.
      values: { xs: 0, sm: 600, mobile: 768, md: 900, lg: 1200, xl: 1536 },
    },
    palette: {
      mode: scheme,
      primary: {
        light: palette.primaryContainer,
        main: palette.primary,
        dark: palette.onPrimaryContainer,
        contrastText: palette.onPrimary,
      },
      secondary: {
        light: palette.secondaryContainer,
        main: palette.secondary,
        dark: palette.onSecondaryContainer,
        contrastText: palette.onSecondary,
      },
      error: {
        light: palette.errorContainer,
        main: palette.error,
        dark: palette.onErrorContainer,
        contrastText: palette.onError,
      },
      // Status accents follow the ODL UI style guide status colors. Unlike
      // primary/secondary/error these keep MUI shade semantics (light = a
      // lighter visible shade, not a tonal container) — soft-variant labels
      // and legend swatches read `.light`/`.dark` as real hues.
      ...(scheme === 'dark'
        ? {
            success: { main: '#4ADE80', light: '#86EFAC', dark: ODL_STYLE_GUIDE.status.active },
            warning: { main: '#FBBF24', light: '#FDE68A', dark: '#D97706' },
            info: { main: '#38BDF8', light: '#7DD3FC', dark: '#0284C7' },
          }
        : {
            success: { main: ODL_STYLE_GUIDE.status.active, light: '#4ADE80', dark: '#15803D' },
            warning: { main: ODL_STYLE_GUIDE.status.warning, light: '#FBBF24', dark: '#B45309' },
            info: { main: '#0284C7', light: '#38BDF8', dark: '#0369A1' },
          }),
      background: {
        default: palette.background,
        paper: palette.surfaceContainerLowest,
      },
      text: {
        primary: palette.onSurface,
        secondary: palette.onSurfaceVariant,
        disabled: palette.interactiveDisabled,
      },
      action: {
        active: palette.onSurfaceVariant,
        hover: palette.interactiveHover,
        selected: palette.interactiveSelected,
        focus: palette.interactiveFocusRing,
        disabled: palette.interactiveDisabled,
        disabledBackground: palette.interactiveDisabledBg,
      },
      divider: palette.divider,
    },
    zIndex: {
      drawer: 1000,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          ${buildDesignTokens()}
          html {
            text-rendering: optimizeLegibility;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          body {
            background-color: var(--surface-page);
            color: var(--text-primary);
            font-family: var(--font-family-sans);
          }
          a {
            color: var(--link-color);
          }
          a:visited {
            color: var(--link-color);
          }
          a:hover,
          a:focus-visible,
          a:active {
            color: var(--link-hover-color);
          }
          a:focus-visible {
            border-radius: var(--radius-sm);
            box-shadow: var(--shadow-focus);
            outline: none;
          }
          /* Thin, iOS-like scrollbars — far less intrusive than the platform
             default (notably the chunky bars on Windows/Linux/Chrome). The
             thumb is an overlay-style rounded pill (padded by a transparent
             border via background-clip) over a transparent track, in a
             scheme-aware colour. Components that hide their own scrollbar
             (e.g. the relay carousels) override this with a more specific
             selector, so they're unaffected. */
          * {
            scrollbar-width: thin;
            scrollbar-color: var(--md-outline-variant) transparent;
          }
          *::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          *::-webkit-scrollbar-track {
            background: transparent;
          }
          *::-webkit-scrollbar-thumb {
            background-color: var(--md-outline-variant);
            border-radius: 10px;
            border: 3px solid transparent;
            background-clip: content-box;
          }
          *::-webkit-scrollbar-thumb:hover {
            background-color: var(--md-outline);
          }
          *::-webkit-scrollbar-corner {
            background: transparent;
          }
        `,
      },
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: {
          root: { backgroundColor: 'var(--skeleton-bg)' },
          rounded: { borderRadius: 'var(--radius-md)' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ ownerState }) => ({
            backgroundColor: 'var(--sidebar-bg)',
            borderColor: 'var(--sidebar-border)',
            boxShadow: `var(--shadow-drawer-${ownerState.anchor || 'left'})`,
          }),
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'var(--surface-primary)',
            color: 'var(--text-primary)',
            boxShadow: 'var(--shadow-header)',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          // Filled is the house input skin (bordered box, inset floating
          // label — no outlined notch striking through the label). Pass
          // variant="outlined" per-field to opt out.
          variant: 'filled',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-border-hover)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--input-focus-border)',
            },
          },
          notchedOutline: {
            borderColor: 'var(--input-border)',
          },
        },
      },
      MuiFilledInput: {
        defaultProps: { disableUnderline: true },
        styleOverrides: {
          root: filledInputRootStyle,
          input: {
            paddingTop: 18,
            paddingBottom: 6,
            paddingLeft: 12,
            paddingRight: 12,
          },
          inputSizeSmall: {
            paddingTop: 16,
            paddingBottom: 4,
          },
          inputHiddenLabel: {
            paddingTop: 8,
            paddingBottom: 8,
            '&.MuiInputBase-inputSizeSmall': {
              paddingTop: 6,
              paddingBottom: 6,
            },
          },
        },
      },
      // Standalone FormControl+Select trios and bare Selects follow the same
      // filled default as MuiTextField; per-field variant overrides opt out.
      MuiFormControl: {
        defaultProps: { variant: 'filled' },
      },
      MuiSelect: {
        defaultProps: { variant: 'filled' },
      },
      // MUI X pickers render their own field component (PickersTextField),
      // which does not read MuiTextField defaults — mirror the filled skin
      // so date/time fields match every other input.
      MuiPickersTextField: {
        defaultProps: { variant: 'filled' },
      },
      MuiPickersFilledInput: {
        defaultProps: { disableUnderline: true },
        styleOverrides: {
          root: filledInputRootStyle,
          // Match MuiFilledInput's input paddings (18/6, small 16/4,
          // hiddenLabel 8/8 and 6/6) so picker sections line up with
          // sibling text fields.
          sectionsContainer: ({ ownerState }) => {
            const isSmall = ownerState.inputSize === 'small';
            if (ownerState.hiddenLabel) {
              return isSmall ? { paddingTop: 6, paddingBottom: 6 } : { paddingTop: 8, paddingBottom: 8 };
            }
            return isSmall ? { paddingTop: 16, paddingBottom: 4 } : { paddingTop: 18, paddingBottom: 6 };
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            '&.MuiFilledInput-root': {
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 4,
              paddingRight: 0,
              '& .MuiAutocomplete-input': {
                paddingTop: 16,
                paddingBottom: 4,
                paddingLeft: 8,
              },
              '&.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                paddingTop: 14,
                paddingBottom: 2,
              },
              '&.MuiInputBase-hiddenLabel .MuiAutocomplete-input': {
                paddingTop: 8,
                paddingBottom: 8,
              },
              '&.MuiInputBase-hiddenLabel.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                paddingTop: 6,
                paddingBottom: 6,
              },
            },
          },
          paper: {
            backgroundColor: 'var(--surface-primary)',
            border: 0,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-dropdown)',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
          input: {
            '&::placeholder': {
              color: 'var(--input-placeholder)',
              opacity: 1,
            },
            '&.Mui-disabled': {
              color: 'var(--input-disabled-color)',
              WebkitTextFillColor: 'var(--input-disabled-color)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          asterisk: requiredDotAsterisk,
          root: {
            fontSize: '0.875rem',
          },
          filled: ({ theme: t }) => ({
            transform: 'translate(12px, 14px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(12px, 4px) scale(0.75)',
              color: t.palette.text.secondary,
              fontWeight: 400,
              '&.Mui-focused': { color: t.palette.primary.main },
            },
            '&.MuiInputLabel-sizeSmall': {
              transform: 'translate(12px, 12px) scale(1)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(12px, 3px) scale(0.75)',
              },
            },
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: requiredDotAsterisk,
          root: ({ theme: t }) => ({
            color: 'var(--text-secondary)',
            '&.Mui-focused': { color: t.palette.primary.main },
            '&.Mui-error': { color: t.palette.error.main },
            '&.Mui-disabled': { color: 'var(--input-disabled-color)' },
          }),
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
          label: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            ...focusVisible(),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        variants: ['primary', 'secondary', 'error', 'warning', 'info', 'success'].map(c => ({
          props: { variant: 'soft', color: c },
          style: ({ theme: t }) => {
            const main = t.palette[c].main;
            const usesM3Container = ['primary', 'secondary', 'error'].includes(c);
            const label =
              c === 'warning'
                ? 'var(--status-warning-text)'
                : usesM3Container
                  ? t.palette[c].dark
                  : t.palette.mode === 'dark'
                    ? t.palette[c].light
                    : t.palette[c].dark;
            return {
              backgroundColor: alpha(main, t.palette.mode === 'dark' ? 0.18 : 0.1),
              color: label,
              border: '1px solid transparent',
              boxShadow: 'var(--button-shadow)',
              '&:hover': {
                backgroundColor: alpha(main, t.palette.mode === 'dark' ? 0.24 : 0.16),
                boxShadow: 'var(--button-shadow-hover)',
              },
              '&:active': {
                boxShadow: 'var(--button-shadow-active)',
              },
              ...focusVisible('var(--button-shadow)'),
              '& .MuiTouchRipple-root': { color: alpha(main, 0.5) },
              '&.Mui-disabled': { boxShadow: 'none', borderColor: 'transparent' },
            };
          },
        })),
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 'var(--font-sm)',
            padding: '7px 14px',
            borderRadius: 'var(--button-radius)',
            ...raisedSurface,
            '&.Mui-disabled': {
              backgroundColor: 'var(--button-disabled-bg)',
              color: 'var(--button-disabled-color)',
              borderColor: 'transparent',
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: 'var(--button-shadow)',
            '&:hover': {
              boxShadow: 'var(--button-shadow-hover)',
            },
            '&:active': {
              boxShadow: 'var(--button-shadow-active)',
            },
            ...focusVisible('var(--button-shadow)'),
            '&.Mui-disabled': {
              backgroundColor: 'var(--button-disabled-bg)',
              color: 'var(--button-disabled-color)',
              boxShadow: 'none',
            },
          },
          outlined: {
            borderWidth: 1,
            borderColor: 'var(--button-outline-border)',
            backgroundColor: 'var(--surface-primary)',
            color: 'var(--text-primary)',
            '&:hover': {
              borderWidth: 1,
              borderColor: 'var(--button-outline-border-hover)',
              backgroundColor: 'var(--interactive-hover)',
            },
            ...focusVisible('var(--button-shadow)'),
            '&.Mui-disabled': {
              backgroundColor: 'var(--button-disabled-bg)',
              borderColor: 'var(--button-disabled-color)',
              color: 'var(--button-disabled-color)',
            },
          },
          text: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: 'var(--interactive-hover)',
            },
            '&:active': {
              boxShadow: 'none',
              transform: 'none',
            },
            ...focusVisible(),
          },
          sizeSmall: {
            padding: '5px 10px',
            fontSize: '0.8125rem',
          },
          sizeLarge: {
            padding: '9px 18px',
            fontSize: '0.9375rem',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--button-radius)',
            ...focusVisible(),
            // When rendered as <a> (e.g. component={Link}), the global
            // a:visited rule wins over MUI's color: inherit because of
            // pseudo-class specificity. Re-assert inherit at higher
            // specificity so visited links don't take on the link blue.
            '&:visited': { color: 'inherit' },
          },
        },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: 'medium',
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            gap: 2,
            padding: 2,
            borderRadius: 'var(--filter-chip-radius)',
            backgroundColor: 'var(--surface-secondary)',
            boxShadow: 'var(--shadow-inset)',
            '& .MuiToggleButtonGroup-grouped': {
              margin: 0,
              border: '0 !important',
              borderRadius: 'calc(var(--filter-chip-radius) - 2px) !important',
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            color: 'var(--text-primary)',
            border: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgb(var(--md-primary-rgb) / 0.07)',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--surface-primary)',
              boxShadow: 'var(--shadow-sm)',
              color: 'var(--md-primary)',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: 'var(--surface-primary)',
                boxShadow: 'var(--shadow-md)',
              },
            },
            ...focusVisible(),
            '&.Mui-disabled': {
              boxShadow: 'none',
            },
          },
          sizeSmall: {
            height: 'var(--filter-chip-height)',
            lineHeight: 1,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 12,
            paddingRight: 12,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--status-chip-radius)',
            fontWeight: 600,
            fontSize: '0.75rem',
            '&.MuiChip-clickable': {
              transition:
                'background-color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard), box-shadow var(--duration-fast) var(--easing-standard)',
              '&:hover': {
                boxShadow: 'var(--shadow-sm)',
              },
              '&:active': {
                boxShadow: 'var(--button-shadow-active)',
              },
              ...focusVisible(),
            },
          },
          sizeSmall: {
            height: 'var(--status-chip-height)',
            fontSize: 'var(--status-chip-font-size)',
          },
          outlinedDefault: {
            backgroundColor: 'var(--surface-page)',
            borderColor: 'var(--button-outline-border)',
            color: 'var(--text-primary)',
          },
          filled: {
            border: '1px solid transparent',
          },
          filledDefault: {
            backgroundColor: 'var(--status-neutral-tint)',
            borderColor: 'var(--status-neutral-border)',
            color: 'var(--status-neutral-text)',
          },
          // primary/secondary/error are M3-mapped here: `.light` is the tonal
          // container and `.dark` the matching on-container text, so a tonal
          // chip stays high-contrast in both light and dark schemes.
          filledPrimary: ({ theme: t }) => ({
            backgroundColor: t.palette.primary.light,
            borderColor: alpha(t.palette.primary.main, 0.2),
            color: t.palette.primary.dark,
          }),
          filledSecondary: ({ theme: t }) => ({
            backgroundColor: t.palette.secondary.light,
            borderColor: alpha(t.palette.secondary.main, 0.2),
            color: t.palette.secondary.dark,
          }),
          filledError: {
            backgroundColor: 'var(--status-bad-tint)',
            borderColor: 'var(--status-bad-border)',
            color: 'var(--status-bad-text)',
          },
          filledSuccess: {
            backgroundColor: 'var(--status-ok-tint)',
            borderColor: 'var(--status-ok-border)',
            color: 'var(--status-ok-text)',
          },
          filledWarning: {
            backgroundColor: 'var(--status-warning-tint)',
            borderColor: 'var(--status-warning-border)',
            color: 'var(--status-warning-text)',
          },
          filledInfo: {
            backgroundColor: 'var(--status-info-tint)',
            borderColor: 'var(--status-info-border)',
            color: 'var(--status-info-text)',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            gap: 8,
          },
        },
      },
      MuiSwitch: {
        defaultProps: { disableRipple: true },
        styleOverrides: {
          // Honor ``ownerState.size``. Medium (default) is 56x32 with
          // a 20x20 thumb that translates 24px when checked. ``small``
          // shrinks proportionally to 40x24 with a 14x14 thumb that
          // translates 16px. Without this, ``<Switch size="small">``
          // inherits the medium track width but MUI's small thumb
          // positioning, leaving the thumb stuck at the far left of
          // an oversized rail.
          root: ({ ownerState }) => {
            const small = ownerState?.size === 'small';
            return {
              width: small ? 40 : 56,
              height: small ? 24 : 32,
              padding: small ? 4 : 6,
              margin: 4,
              overflow: 'visible',
            };
          },
          switchBase: ({ theme: t, ownerState }) => {
            const small = ownerState?.size === 'small';
            return {
              padding: small ? 4 : 6,
              transitionDuration: 'var(--duration-slow)',
              '&.Mui-checked': {
                transform: `translateX(${small ? 16 : 24}px)`,
                color: t.palette.primary.contrastText,
                '& + .MuiSwitch-track': {
                  backgroundColor: t.palette.primary.main,
                  borderColor: t.palette.primary.main,
                  opacity: 1,
                },
              },
              '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
            };
          },
          thumb: ({ theme: t, ownerState }) => {
            const small = ownerState?.size === 'small';
            const dim = small ? 14 : 20;
            const maskSize = small ? '10px 10px' : '14px 14px';
            return {
              width: dim,
              height: dim,
              borderRadius: 4,
              border: 0,
              boxShadow: `0 1px 3px ${alpha(t.palette.common.black, 0.22)}`,
              backgroundColor: t.palette.common.white,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 2,
                backgroundColor: 'currentColor',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                maskSize,
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                WebkitMaskSize: maskSize,
                maskImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='currentColor' d='M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z'/></svg>\")",
                WebkitMaskImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='currentColor' d='M19 6.4L17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z'/></svg>\")",
                color: t.palette.text.secondary,
              },
              '.Mui-checked &::after': {
                maskImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='currentColor' d='M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'/></svg>\")",
                WebkitMaskImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><path fill='currentColor' d='M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'/></svg>\")",
                color: t.palette.primary.main,
              },
            };
          },
          track: ({ theme: t }) => ({
            borderRadius: 6,
            border: '1px solid transparent',
            backgroundColor: alpha(t.palette.text.primary, 0.06),
            boxShadow: `inset 0 1px 2px ${alpha(t.palette.common.black, t.palette.mode === 'dark' ? 0.36 : 0.12)}`,
            opacity: 1,
            transition:
              'background-color var(--duration-slow) var(--easing-standard), border-color var(--duration-slow) var(--easing-standard)',
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: 0,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-dropdown)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: 0,
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-dropdown)',
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'var(--surface-primary)',
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: 'var(--border-subtle)',
            boxShadow: 'var(--shadow-sm)',
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--card-border)',
            borderRadius: 'var(--card-radius)',
            boxShadow: 'var(--card-shadow)',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 'var(--card-padding)',
            '&:last-child': {
              paddingBottom: 'var(--card-padding)',
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: t.palette.divider,
          }),
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: 'var(--font-sm)',
            fontWeight: 'var(--list-item-primary-weight, var(--weight-medium))',
            color: 'var(--list-item-primary-color, var(--text-primary))',
          },
          secondary: ({ theme: t }) => ({
            fontSize: 'var(--font-sm)',
            color: t.palette.text.secondary,
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: 'var(--md-primary-container)',
              color: 'var(--md-on-primary-container)',
              '&:hover': {
                backgroundColor: 'var(--md-primary-container)',
              },
            },
            // Nav items mark the active route with aria-current="page"; the
            // theme paints the indicator bar so callsites never hand-roll one.
            '&[aria-current="page"]': {
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: '55%',
                width: 3,
                borderRadius: 999,
                backgroundColor: 'var(--nav-active-indicator)',
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: 'var(--md-primary-container)',
              color: 'var(--md-on-primary-container)',
              '&:hover': {
                backgroundColor: 'var(--md-primary-container)',
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'var(--md-primary-container)',
              },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: 'var(--surface-primary)',
            border: 0,
            boxShadow: 'var(--shadow-lg)',
            borderRadius: 'var(--radius-xl)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: '1.125rem',
            fontWeight: 650,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 2,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 48,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderColor: t.palette.divider,
          }),
          head: ({ theme: t }) => ({
            fontWeight: 650,
            backgroundColor: 'var(--grid-header-bg)',
            color: t.palette.text.primary,
            textTransform: 'none',
            fontSize: '0.8125rem',
            letterSpacing: 0,
          }),
        },
      },
      MuiDataGrid: {
        defaultProps: {
          columnHeaderHeight: 40,
        },
        styleOverrides: {
          root: {
            border: 0,
            borderRadius: 'var(--grid-radius)',
            // MUI also resolves `styles.root` onto its full-size ScrollShadow
            // slots, so surface paint and elevation belong on the root class.
            '&.MuiDataGrid-root': {
              backgroundColor: 'var(--surface-primary)',
              boxShadow: 'var(--card-shadow)',
            },
            '--DataGrid-rowBorderColor': 'var(--grid-cell-border)',
            '--DataGrid-containerBackground': 'var(--grid-header-bg)',
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: '2px solid var(--md-primary)',
              outlineOffset: -2,
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: '2px solid var(--md-primary)',
              outlineOffset: -2,
            },
            '@media (forced-colors: active)': {
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
                {
                  outlineColor: 'Highlight',
                },
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'var(--grid-row-hover)' },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'var(--grid-row-selected)',
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: 'var(--grid-row-selected-hover)',
            },
            // The divider is what tells a reader a column is frozen rather
            // than merely first or last, so each edge draws it on the side
            // the scrolling columns pass under.
            '& .MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--pinnedLeft.MuiDataGrid-columnHeader--withRightBorder':
              {
                boxShadow: '1px 0 0 var(--grid-pinned-divider)',
              },
            '& .MuiDataGrid-cell--pinnedRight.MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--pinnedRight.MuiDataGrid-columnHeader--withLeftBorder':
              {
                boxShadow: '-1px 0 0 var(--grid-pinned-divider)',
              },
            // Vertically center cell content in auto-height rows
            // (getRowHeight: () => 'auto'). MUI centers fixed-height cells via
            // line-height but leaves dynamic-height cells top-aligned; this
            // makes every grid's auto-height rows center consistently.
            '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-virtualScroller': {
              scrollbarGutter: 'stable',
            },
            '& .MuiDataGrid-scrollbar--vertical': {
              width: '10px',
            },
            '& .MuiDataGrid-scrollbar': {
              '&::-webkit-scrollbar': {
                width: '10px',
                height: '10px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--md-outline-variant)',
                borderRadius: '5px',
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'var(--md-outline)',
                backgroundClip: 'content-box',
                border: '2px solid transparent',
              },
            },
            '& .MuiDataGrid-topContainer .MuiDataGrid-filler': {
              backgroundColor: 'var(--grid-header-bg)',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller': {
              backgroundColor: 'var(--grid-header-bg)',
              borderTopRightRadius: 'var(--grid-radius)',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              marginLeft: '6px',
            },
            '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              padding: '2px',
              '&:hover': {
                backgroundColor: 'rgb(var(--md-primary-rgb) / 0.04)',
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
                transform: 'none',
              },
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'var(--grid-header-color)',
              opacity: 0.85,
            },
            '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'rgb(var(--md-primary-rgb) / 0.04)',
                boxShadow: 'none',
              },
            },
            '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator, & .MuiDataGrid-columnHeader:focus-within .MuiDataGrid-columnSeparator':
              {
                color: 'var(--md-outline)',
              },
          },
          columnHeaders: {
            backgroundColor: 'var(--grid-header-bg)',
            borderBottom: 0,
            boxShadow: 'var(--grid-header-shadow)',
          },
          columnHeader: {
            backgroundColor: 'var(--grid-header-bg)',
          },
          // A header states what the column is, so it wraps rather than
          // truncating: an ellipsis in a header hides the one word that
          // distinguishes two similar columns, and the reader cannot widen a
          // column to recover it on a touch device.
          columnHeaderTitle: {
            fontWeight: 650,
            fontSize: '0.8125rem',
            color: 'var(--grid-header-color)',
            whiteSpace: 'normal',
            lineHeight: 1.25,
            overflow: 'visible',
            textOverflow: 'clip',
          },
          columnHeaderTitleContainer: {
            overflow: 'visible',
          },
          columnHeaderTitleContainerContent: {
            overflow: 'visible',
          },
          columnSeparator: {
            color: 'transparent',
          },
          cell: {
            borderColor: 'var(--grid-cell-border)',
            fontSize: 'var(--font-sm)',
            lineHeight: 1.45,
            fontVariantNumeric: 'tabular-nums',
          },
          row: {
            '&:hover': {
              backgroundColor: 'var(--grid-row-hover)',
            },
            '&.Mui-selected': {
              backgroundColor: 'var(--grid-row-selected)',
              '&:hover': {
                backgroundColor: 'var(--grid-row-selected-hover)',
              },
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'var(--interactive-hover)',
            },
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'var(--interactive-selected)',
            },
            '&:last-child td': {
              borderBottom: 0,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: 'var(--link-color)',
            textDecorationColor: 'transparent',
            '&:visited': {
              color: 'var(--link-color)',
            },
            '&:hover': {
              color: 'var(--link-hover-color)',
              textDecorationColor: 'currentColor',
            },
            '&.Mui-focusVisible, &:focus-visible': {
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow-focus)',
              outline: 'none',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme: t }) => ({
            backgroundColor: t.palette.mode === 'dark' ? t.palette.background.paper : grey[800],
            color: t.palette.mode === 'dark' ? t.palette.text.primary : t.palette.common.white,
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 'var(--radius-sm)',
            padding: '6px 10px',
          }),
          arrow: ({ theme: t }) => ({
            color: t.palette.mode === 'dark' ? t.palette.background.paper : grey[800],
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-md)',
          },
          standardSuccess: {
            backgroundColor: 'var(--status-ok-tint)',
            border: '1px solid var(--status-ok-border)',
            color: 'var(--status-ok-text)',
          },
          standardError: {
            backgroundColor: 'var(--status-bad-tint)',
            border: '1px solid var(--status-bad-border)',
            color: 'var(--status-bad-text)',
          },
          standardWarning: {
            backgroundColor: 'var(--status-warning-tint)',
            border: '1px solid var(--status-warning-border)',
            color: 'var(--status-warning-text)',
          },
          standardInfo: {
            backgroundColor: 'var(--status-info-tint)',
            border: '1px solid var(--status-info-border)',
            color: 'var(--status-info-text)',
          },
        },
      },
    },
  });

  theme.typography.h5 = {
    fontSize: '1.5rem',
    fontWeight: 650,
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.25rem',
      fontWeight: 700,
    },
  };

  theme.components.MuiInputBase.styleOverrides.root = {
    fontSize: 'var(--font-md)',
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
  };
  theme.components.MuiInputLabel.styleOverrides.root = {
    fontSize: '0.875rem',
    color: 'var(--text-secondary)',
    // Legacy global CSS lowers labels below menus. Opaque themed input trays
    // need the label above their paint layer; portal menus remain far above it.
    zIndex: '1 !important',
    '&.Mui-focused': { color: theme.palette.primary.main },
    '&.Mui-error': { color: theme.palette.error.main },
    '&.Mui-disabled': { color: 'var(--input-disabled-color)' },
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
  };
  // Outlined controls follow the style-guide specimen: a white surface with a
  // crisp neutral edge, primary focus edge, and pale supplemental focus halo.
  theme.components.MuiOutlinedInput.styleOverrides.root = {
    backgroundColor: 'var(--input-bg)',
    boxShadow: 'var(--input-shadow)',
    transition:
      'background-color var(--duration-normal) var(--easing-standard), border-color var(--duration-normal) var(--easing-standard), box-shadow var(--duration-normal) var(--easing-standard)',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--input-border)',
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--input-border-hover)',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'var(--input-bg)',
      boxShadow: 'var(--input-focus-shadow), var(--input-shadow)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--input-focus-border)',
        borderWidth: 1,
      },
    },
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main,
      },
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.18)}, var(--input-shadow)`,
      },
    },
    '&.Mui-disabled': {
      backgroundColor: 'var(--input-disabled-bg)',
      color: 'var(--input-disabled-color)',
      boxShadow: 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'var(--input-disabled-color)',
      },
    },
    '@media (forced-colors: active)': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'CanvasText',
      },
    },
    [theme.breakpoints.down('md')]: {
      '& .MuiOutlinedInput-input': {
        padding: '12px 14px',
      },
    },
  };

  return theme;
};

const theme = createOutdoorlinkTheme();

export default theme;
